/**
 * Colour arithmetic for the workspace ramp, so a brand kit can never make a
 * workspace unreadable.
 *
 * Why this exists. A kit carries six colour pairs and the workspace wears them as
 * they are: `--ws-accent` reads the kit's brand colour directly, and that same
 * colour is used both as a **fill** (buttons, the active rail row) and as **text**
 * (links, the active nav item, the conduct notice, the unread dot). The six house
 * kits were hand-picked so the two uses never collided. An admin-made kit is not:
 * the owner's own kit is a pale tan, and on production every accent-coloured word
 * on the page was unreadable pale yellow on a light surface (report OR-1,
 * 2026-09-21).
 *
 * CSS cannot branch on contrast — there is no `contrast-color()` in the browsers
 * this ships to — so the branch is here, in plain functions over plain values, and
 * the result is injected as two extra custom properties beside the kit's twelve.
 * Nothing about the stored kit changes: the snapshot on the trial is frozen and
 * stays frozen, and this derives at render time.
 *
 * Everything below is pure and has no DOM in it.
 */

/** WCAG AA for body and label text. The one number this module aims at. */
export const AA_CONTRAST = 4.5;

const clamp = (value, low, high) => Math.min(high, Math.max(low, value));

const HEX = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const RGB = /^rgba?\(([^)]+)\)$/i;
const HSL = /^hsla?\(([^)]+)\)$/i;

/** The numbers inside a functional notation, in either the comma or the space form. */
const partsOf = (body) => String(body)
    .replace(/\//g, ' ')
    .split(/[\s,]+/)
    .filter(Boolean);

const fromHex = (hex) => {
    let digits = hex.slice(1);
    if (digits.length === 3 || digits.length === 4) {
        digits = digits.split('').map((digit) => digit + digit).join('');
    }
    return {
        r: parseInt(digits.slice(0, 2), 16),
        g: parseInt(digits.slice(2, 4), 16),
        b: parseInt(digits.slice(4, 6), 16),
    };
};

const channel = (token) => {
    const text = String(token).trim();
    if (text.endsWith('%')) return clamp((parseFloat(text) / 100) * 255, 0, 255);
    return clamp(parseFloat(text), 0, 255);
};

const hueToChannel = (p, q, t) => {
    let h = t;
    if (h < 0) h += 1;
    if (h > 1) h -= 1;
    if (h < 1 / 6) return p + ((q - p) * 6 * h);
    if (h < 1 / 2) return q;
    if (h < 2 / 3) return p + ((q - p) * ((2 / 3) - h) * 6);
    return p;
};

/**
 * `{r, g, b}` in 0–255, or `null` for a syntax this module cannot read.
 *
 * Hex, `rgb()` and `hsl()` are the three a kit is authored in. The wider colour
 * functions the kit validator admits (`lab`, `oklch`, …) are deliberately not
 * parsed: a value this cannot read falls back to the safe neutral ink rather than
 * being guessed at, which is the owner's own instruction.
 *
 * @param {string} value
 * @returns {{r: number, g: number, b: number}|null}
 */
export const parseColor = (value) => {
    const text = String(value || '').trim();
    if (HEX.test(text)) return fromHex(text);

    const rgb = text.match(RGB);
    if (rgb) {
        const [r, g, b] = partsOf(rgb[1]);
        if (r === undefined || g === undefined || b === undefined) return null;
        const parsed = { r: channel(r), g: channel(g), b: channel(b) };
        return Number.isNaN(parsed.r + parsed.g + parsed.b) ? null : parsed;
    }

    const hsl = text.match(HSL);
    if (hsl) {
        const [h, s, l] = partsOf(hsl[1]);
        if (h === undefined || s === undefined || l === undefined) return null;
        const hue = (((parseFloat(h) % 360) + 360) % 360) / 360;
        const sat = clamp(parseFloat(s) / 100, 0, 1);
        const light = clamp(parseFloat(l) / 100, 0, 1);
        if (Number.isNaN(hue + sat + light)) return null;
        return hslToRgb(hue, sat, light);
    }

    return null;
};

/** `{h, s, l}` with h in 0–1, from 0–255 channels. */
export const rgbToHsl = ({ r, g, b }) => {
    const rr = r / 255;
    const gg = g / 255;
    const bb = b / 255;
    const max = Math.max(rr, gg, bb);
    const min = Math.min(rr, gg, bb);
    const l = (max + min) / 2;

    if (max === min) return { h: 0, s: 0, l };

    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h;
    if (max === rr) h = ((gg - bb) / d) + (gg < bb ? 6 : 0);
    else if (max === gg) h = ((bb - rr) / d) + 2;
    else h = ((rr - gg) / d) + 4;

    return { h: h / 6, s, l };
};

/** The inverse, in the same units. */
export const hslToRgb = (h, s, l) => {
    if (s === 0) {
        const v = Math.round(l * 255);
        return { r: v, g: v, b: v };
    }
    const q = l < 0.5 ? l * (1 + s) : l + s - (l * s);
    const p = (2 * l) - q;
    return {
        r: Math.round(hueToChannel(p, q, h + (1 / 3)) * 255),
        g: Math.round(hueToChannel(p, q, h) * 255),
        b: Math.round(hueToChannel(p, q, h - (1 / 3)) * 255),
    };
};

/** `#rrggbb`, which is what goes into a custom property. */
export const toHex = ({ r, g, b }) => `#${[r, g, b]
    .map((value) => clamp(Math.round(value), 0, 255).toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`;

/** WCAG relative luminance. */
export const relativeLuminance = ({ r, g, b }) => {
    const linear = [r, g, b].map((value) => {
        const v = clamp(value, 0, 255) / 255;
        return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
    });
    return (0.2126 * linear[0]) + (0.7152 * linear[1]) + (0.0722 * linear[2]);
};

/** The WCAG ratio between two opaque colours, 1–21. */
export const contrastRatio = (a, b) => {
    const la = relativeLuminance(a);
    const lb = relativeLuminance(b);
    const light = Math.max(la, lb);
    const dark = Math.min(la, lb);
    return (light + 0.05) / (dark + 0.05);
};

/**
 * `color-mix(in srgb, top <pct>%, base)`, in the same space the sheet mixes in.
 *
 * The ramp's surfaces are mixes of a kit input into a fixed neutral, and this file
 * has to know what a word will actually stand on. The percentages are the sheet's
 * and are named at each call site in `brandKit.js`.
 */
export const mixSrgb = (top, base, percent) => {
    const weight = clamp(percent, 0, 100) / 100;
    return {
        r: (top.r * weight) + (base.r * (1 - weight)),
        g: (top.g * weight) + (base.g * (1 - weight)),
        b: (top.b * weight) + (base.b * (1 - weight)),
    };
};

/**
 * The same colour, moved along its own lightness until it reads on every ground
 * it has to stand on — or `null` when no lightness of that hue can.
 *
 * Hue and saturation are held, so the word still reads as the brand: a pale tan
 * becomes a darker tan, never a grey and never somebody else's colour. Light theme
 * darkens, dark theme lightens, which is the only direction that can help on a
 * light or a dark ground respectively. The walk is over 1 % steps of lightness,
 * which is finer than the eye and is bounded at a hundred iterations.
 *
 * @param {string} value - The kit's own colour
 * @param {Array<{r: number, g: number, b: number}>} grounds - Every surface it sits on
 * @param {'light'|'dark'} theme
 * @param {number} [target]
 * @returns {string|null} - `#rrggbb`, or null when the hue cannot get there
 */
export const readableInk = (value, grounds, theme, target = AA_CONTRAST) => {
    const parsed = parseColor(value);
    if (!parsed || !grounds.length) return null;

    const clears = (colour) => grounds.every((ground) => contrastRatio(colour, ground) >= target);
    if (clears(parsed)) return toHex(parsed);

    const { h, s, l } = rgbToHsl(parsed);
    const step = theme === 'dark' ? 0.01 : -0.01;

    for (let i = 1; i <= 100; i += 1) {
        const lightness = l + (step * i);
        if (lightness < 0 || lightness > 1) break;
        const moved = hslToRgb(h, s, lightness);
        if (clears(moved)) return toHex(moved);
    }

    return null;
};

/**
 * Which of two inks to put on a fill, when the kit's own will not do.
 *
 * Near-black and white rather than pure black: the ramp's own ink ends are what the
 * rest of the workspace is written in, so a button that has to give up the kit's
 * contrast colour still lands in the product's palette.
 */
export const NEAR_BLACK = '#111315';
export const NEAR_WHITE = '#FFFFFF';

/**
 * The kit's own on-accent colour if it reads on the accent fill, else whichever of
 * near-black and white reads better on it.
 *
 * @param {string} onColor - The kit's `accent_contrast` for this theme
 * @param {string} accent - The kit's `accent` for this theme
 * @param {number} [target]
 * @returns {string} - A colour value; the kit's own string where it is kept
 */
export const readableOnAccent = (onColor, accent, target = AA_CONTRAST) => {
    const fill = parseColor(accent);
    if (!fill) return onColor;

    const own = parseColor(onColor);
    if (own && contrastRatio(own, fill) >= target) return onColor;

    const black = parseColor(NEAR_BLACK);
    const white = parseColor(NEAR_WHITE);
    return contrastRatio(black, fill) >= contrastRatio(white, fill) ? NEAR_BLACK : NEAR_WHITE;
};
